/**
 * Chapter Four — The Studio.
 * A quiet editorial storytelling section: real human portraits, a hero
 * studio video moment, and Elin's own words. Warm paper tone continues
 * from the sketchbook chapter so the transition feels seamless.
 */

const team = [
  {
    name: "Elin Vahr",
    role: "Founder · Principal Designer",
    bio: "Trained as an architect in Copenhagen, apprenticed with Ingo Maurer in Munich, then walked her own line. Believes light is the first material of any room.",
    image:
      "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20thoughtful%20middle%20aged%20female%20lighting%20designer%20with%20natural%20blonde%20hair%20wearing%20a%20soft%20linen%20oversized%20blazer%2C%20sitting%20by%20a%20warm%20lit%20architects%20drafting%20table%20holding%20pencil%20sketches%2C%20late%20afternoon%20golden%20window%20light%20from%20the%20side%2C%20warm%20cream%20plaster%20wall%20background%2C%20natural%20skin%20texture%2C%20unretouched%2C%20cinematic%20contemplative%20mood%2C%20fine%20film%20grain%2C%20editorial%20interior%20photography%20aesthetic%2C%20muted%20warm%20tones&width=900&height=1200&seq=studio-portrait-elin-01&orientation=portrait",
  },
  {
    name: "Tomás Rey",
    role: "Head of Technical Design",
    bio: "Electrical engineer by training, lighting technician by heart. Spent a decade tuning European theatre rigs before joining the studio in 2020. Handles every dimming curve like a musician.",
    image:
      "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20focused%20middle%20aged%20latin%20male%20lighting%20engineer%20wearing%20a%20dark%20denim%20workshirt%2C%20standing%20in%20a%20warm%20lit%20design%20studio%20workshop%20holding%20a%20small%20brass%20light%20fitting%2C%20soft%20window%20daylight%20on%20a%20muted%20clay%20wall%20background%2C%20subtle%20warm%20tones%2C%20natural%20skin%20texture%2C%20unretouched%2C%20cinematic%20quiet%20mood%2C%20fine%20film%20grain%2C%20editorial%20interior%20photography%20aesthetic&width=900&height=1200&seq=studio-portrait-tomas-02&orientation=portrait",
  },
  {
    name: "Ida Nakamura",
    role: "Studio Director · Materials",
    bio: "Sources every shell, every diffuser, every hand-blown drop. From Kyoto glass workshops to Ligurian brass casters. Keeps the studio's material library in a single ancient wooden cabinet.",
    image:
      "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20quietly%20confident%20japanese%20female%20studio%20director%20wearing%20a%20warm%20terracotta%20knit%20sweater%2C%20holding%20a%20small%20piece%20of%20hand%20blown%20amber%20glass%20up%20to%20soft%20natural%20window%20light%2C%20warm%20cream%20plaster%20studio%20wall%20background%2C%20late%20afternoon%20golden%20side%20light%2C%20natural%20skin%20texture%2C%20unretouched%2C%20cinematic%20contemplative%20mood%2C%20fine%20film%20grain%2C%20editorial%20interior%20photography%20aesthetic&width=900&height=1200&seq=studio-portrait-ida-03&orientation=portrait",
  },
];

export default function StoryChapter() {
  return (
    <section
      className="relative w-full"
      style={{ backgroundColor: "#F5EFE4", color: "#1B1815" }}
    >
      {/* Ambient paper grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 30%, rgba(0,0,0,0) 40%, rgba(120,90,50,0.08) 100%)",
        }}
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10 lg:px-14 py-24 md:py-32">
        {/* Chapter label */}
        <div className="flex items-center gap-3 mb-10 md:mb-14">
          <span className="w-8 h-px bg-[#1B1815]/60" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#5A544C] whitespace-nowrap">
            Chapter Four · The Studio
          </span>
        </div>

        {/* Editorial title + intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-end mb-16 md:mb-24">
          <div className="lg:col-span-8">
            <h2
              className="font-black uppercase leading-[0.88] tracking-[-0.03em]"
              style={{ fontSize: "clamp(44px, 6.6vw, 108px)" }}
            >
              THREE PEOPLE,
              <br />
              <span className="italic font-light lowercase tracking-tight text-[#5A544C]">
                one long
              </span>{" "}
              CONVERSATION
              <br />
              WITH LIGHT.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-base md:text-lg leading-relaxed text-[#3A342E] max-w-md">
              We are a small studio, on purpose. Every project passes through
              all three of us — brief, drawing, mockup, install, tuning.
              Nothing is delegated to a stranger. Nothing goes out cold.
            </p>
            <div className="mt-6 text-[10px] uppercase tracking-[0.35em] text-[#5A544C]">
              Est. 2018 · Copenhagen & Lisbon
            </div>
          </div>
        </div>

        {/* Cinematic studio reel — silent looping video moment */}
        <div className="relative w-full rounded-md overflow-hidden mb-16 md:mb-24 bg-[#1B1815]">
          <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              poster="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20warm%20contemporary%20lighting%20design%20studio%20at%20golden%20hour%2C%20suspended%20brass%20pendant%20lamps%20glowing%20softly%20above%20a%20long%20wooden%20drafting%20table%20covered%20with%20pencil%20sketches%20and%20fabric%20swatches%2C%20late%20afternoon%20window%20light%20streaming%20across%20a%20limewash%20plaster%20wall%2C%20fine%20film%20grain%2C%20editorial%20architectural%20interior%20photography%2C%20muted%20warm%20amber%20tones%2C%20high%20detail%20atmospheric%20haze&width=2000&height=1125&seq=studio-reel-poster-04&orientation=landscape"
            >
              <source
                src="https://cdn.pixabay.com/video/2020/05/13/38753-421684250_large.mp4"
                type="video/mp4"
              />
            </video>
            {/* Warm overlay to keep tone consistent */}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#1B1815]/60 via-transparent to-[#1B1815]/20" />
            {/* Caption */}
            <div className="absolute left-6 md:left-10 bottom-6 md:bottom-10 text-white max-w-md">
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/70 mb-2">
                Studio Reel · Winter 2024
              </div>
              <div
                className="font-semibold leading-tight tracking-tight"
                style={{ fontSize: "clamp(18px, 2vw, 28px)" }}
              >
                Between the drawing and the room —
                <br />
                <span className="italic font-light text-white/80">
                  a lot of quiet listening.
                </span>
              </div>
            </div>
            <div className="absolute right-6 md:right-10 top-6 md:top-10 text-white/80 flex items-center gap-2 text-[10px] uppercase tracking-[0.4em]">
              <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />
              Now playing
            </div>
          </div>
        </div>

        {/* Team portraits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {team.map((person) => (
            <article key={person.name} className="flex flex-col">
              <div className="relative w-full overflow-hidden rounded-sm bg-[#EFE7D6]">
                <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
                  <img
                    src={person.image}
                    alt={person.name}
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                </div>
              </div>
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.35em] text-[#5A544C] mb-2">
                  {person.role}
                </div>
                <h3 className="text-[22px] md:text-[26px] font-black tracking-tight leading-tight">
                  {person.name}
                </h3>
                <p className="mt-3 text-[14px] leading-relaxed text-[#3A342E]">
                  {person.bio}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Pullquote */}
        <div className="mt-20 md:mt-28 border-t border-[#1B1815]/15 pt-14 md:pt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#5A544C]">
              A note from Elin
            </div>
          </div>
          <div className="lg:col-span-9">
            <blockquote
              className="italic font-light leading-[1.15] tracking-tight text-[#1B1815]"
              style={{ fontSize: "clamp(24px, 3vw, 42px)" }}
            >
              &ldquo;I don&apos;t design lamps. I design the twenty minutes
              after sunset, when a room decides what kind of evening it wants
              to be. Everything we make is in service of that half hour.&rdquo;
            </blockquote>
            <div className="mt-6 text-[10px] uppercase tracking-[0.35em] text-[#5A544C]">
              — Elin Vahr, Founder
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}